/**
 * Login SMS two-factor authentication, presented by Cognito (custom auth challenge) after the password
 * was verified, when the system wide `login_sms_2fa` setting is enabled.
 */
export type LoginMfaChallengeType = 'MFA_SMS_CODE' | 'MFA_ENROL_PHONE';

export interface LoginMfaChallenge {
  // MFA_ENROL_PHONE: user has no verified phone yet and must add one, MFA_SMS_CODE: enter the SMS code
  type: LoginMfaChallengeType;
  // last 3 digits of the phone the code was sent to (MFA_SMS_CODE only)
  phoneHint: string;
  // why the same challenge is presented again
  error: '' | 'INVALID_CODE' | 'INVALID_PHONE';
}

export interface LoginMfaHandle {
  challenge: LoginMfaChallenge;
  /**
   * Submits the phone number (MFA_ENROL_PHONE) or SMS code (MFA_SMS_CODE).
   * Resolves the next challenge, or null once the user is signed in.
   * Rejects when Cognito ended the attempt (too many wrong answers or session expired), call resend().
   */
  answer(value: string): Promise<LoginMfaChallenge | null>;
  /**
   * Starts a new sign in attempt, which sends the SMS code again
   */
  resend(): Promise<LoginMfaChallenge | null>;
}

/**
 * Shows the 2FA UI, resolves true once signed in, false when the user cancelled
 */
export type LoginMfaPrompt = (handle: LoginMfaHandle) => Promise<boolean>;

export const LOGIN_MFA_CANCELLED_MESSAGE = 'Sign in cancelled: SMS verification is required.';

// error marker thrown by the CreateAuthChallenge / VerifyAuthChallengeResponse lambdas when the phone's country is blocked
const LOGIN_MFA_COUNTRY_BLOCKED_MARKER = 'LOGIN_COUNTRY_BLOCKED';
export const LOGIN_MFA_COUNTRY_BLOCKED_MESSAGE =
  'Your account has been locked. Please contact the administrator.';

/**
 * Replaces the raw Cognito trigger error with a readable one when the phone's country is blocked
 */
export function toLoginMfaError(error: unknown): unknown {
  return String((error as Error)?.message ?? '').includes(LOGIN_MFA_COUNTRY_BLOCKED_MARKER)
    ? new Error(LOGIN_MFA_COUNTRY_BLOCKED_MESSAGE)
    : error;
}
